/**
 * AUDINEXA Demo Deck — slide data.
 *
 * Ground truth for the interactive /demo route.
 * Each slide's `screenshot` maps 1:1 to a file the Playwright capture
 * script writes into /app/frontend/public/demo/.
 *
 * If a screenshot is missing, the slide gracefully falls back to a
 * "captured with real production data" placeholder — no broken images.
 */

const S = (section, idx, title, purpose, objective, screenshot, url) => ({
  section,
  idx,
  title,
  purpose,
  objective,
  screenshot: `/demo/${screenshot}`,
  url,
});

export const SECTIONS = [
  { id: 1, name: 'Patient Journey', accent: '#D95D39', icon: 'Users',
    tagline: 'From walk-in to fitted — one connected story.' },
  { id: 2, name: 'Audiology Suite', accent: '#0EA5A4', icon: 'Stethoscope',
    tagline: 'Every diagnostic protocol, tamper-proof.' },
  { id: 3, name: 'Repair Workflow', accent: '#7C3AED', icon: 'Wrench',
    tagline: 'Zero device lost in vendor limbo.' },
  { id: 4, name: 'Hearing Aids & Inventory', accent: '#2563EB', icon: 'Headphones',
    tagline: 'Serialised · trial-tracked · warranty-ready.' },
  { id: 5, name: 'Revenue Dashboard', accent: '#059669', icon: 'LineChart',
    tagline: 'Live money, not month-end guesses.' },
  { id: 6, name: 'Reports & Analytics', accent: '#DB2777', icon: 'BarChart3',
    tagline: 'Owner insights the receptionist can\'t give you.' },
  { id: 7, name: 'Referral Corner', accent: '#EA580C', icon: 'Handshake',
    tagline: 'Track every ENT source. Pay them on time.' },
  { id: 8, name: 'Settings', accent: '#0891B2', icon: 'Settings',
    tagline: 'Configure once. Ship. Forget.' },
  { id: 9, name: 'Security & Compliance', accent: '#B91C1C', icon: 'ShieldCheck',
    tagline: 'DPDPA-audited. Encrypted at rest. Every action logged.' },
  { id: 10, name: 'Support', accent: '#4F46E5', icon: 'LifeBuoy',
    tagline: 'Onboarding call, chat, and a real human on the phone.' },
];

export const SLIDES = [
  // ── Section 1: Patient Journey (6) ──────────────────────────────
  S(1, 1, 'Patient Master File',
    'A single source of truth — MRD-numbered, searchable, and shared across every module.',
    'Kill the "which Excel tab is Priya in?" question forever. Every consultation, invoice, and hearing test attaches to the patient card automatically.',
    's01-patient-list.png', '/patients'),
  S(1, 2, 'Book an Appointment',
    'Structured scheduling with test-type chips, doctor selection, and duration hints — not free-text calendar chaos.',
    'Front-desk books tests in 15 seconds. The audiologist sees exactly what protocol is coming next hour.',
    's01-appointment-book.png', '/appointments'),
  S(1, 3, 'Diagnostic Workflow — Kanban',
    'A live board showing every patient in "Testing", "Report Pending", "Reviewed", "Delivered".',
    'The audiologist never forgets a signed report. The receptionist knows exactly who to call.',
    's01-test-kanban.png', '/test'),
  S(1, 4, 'Hearing Test Report',
    'Tamper-proof digital audiogram + tympanogram + speech, JSON-snapshot versioned so every visit is a distinct signed report.',
    'Legally defensible artefact. Ready to hand to the ENT, the patient, or an insurance adjudicator.',
    's01-test-report.png', '/reports'),
  S(1, 5, 'Hearing-Aid Quote',
    'Auto-priced quote with GST break-up, trade-in credit, and one-click WhatsApp send.',
    'Convert a diagnostic into a sale conversation in the same visit. Prospects walk out with a quote in hand.',
    's01-ha-quote.png', '/ha'),
  S(1, 6, 'GST Invoice — Sold & Fitted',
    'HSN-coded invoice with CGST/SGST/IGST auto-computed, e-invoice ready, linked back to the audiogram.',
    'Your CA gets a clean CSV. Your patient gets a compliant receipt. You forget invoicing exists.',
    's01-invoice.png', '/billing'),

  // ── Section 2: Audiology Suite (5) ──────────────────────────────
  S(2, 1, 'Pure Tone Audiometry',
    'Full AC/BC entry, masking flags, extended-frequency support, and instant sensori-neural/conductive classification.',
    'Match your booth\'s clinical protocol. Sign the audiogram in one click.',
    's02-pta.png', '/test'),
  S(2, 2, 'Tympanometry & Reflex',
    'Native tympanogram capture with peak pressure, admittance, and reflex threshold entry.',
    'Middle-ear pathology visible at a glance — no scanning a printed strip.',
    's02-tympo.png', '/test'),
  S(2, 3, 'Speech & Word Recognition',
    'SRT, WRS, aided-soundfield entries with automatic best-ear masking rules baked in.',
    'Functional hearing capacity captured next to the audiogram — one signed report.',
    's02-speech.png', '/test'),
  S(2, 4, 'Version History',
    'Every re-visit produces a JSON-snapshot version. Compare Aug audiogram vs Feb baseline in one click.',
    'Monitor progression over years — impossible with paper printouts. Critical for cochlear implant candidacy.',
    's02-history.png', '/reports'),
  S(2, 5, 'Report Delivery',
    'Signed PDF auto-generated in Indian audiology report format — hand-over, WhatsApp, or email.',
    'Referring ENTs get a report they can actually read. Patients get a professional artefact.',
    's02-delivery.png', '/reports'),

  // ── Section 3: Repair Workflow (6) ──────────────────────────────
  S(3, 1, 'Repair Kanban — 13 States',
    'Every ticket lives in exactly one of 13 states from Intake → Delivered. Colour-coded, SLA-badged.',
    'No device gets lost in vendor limbo. Every stalled ticket surfaces on the owner dashboard.',
    's03-repair-kanban.png', '/repair'),
  S(3, 2, 'Intake Form',
    'Serial-linked intake — auto-pulls warranty status, past visits, patient history.',
    'Reception logs a walk-in repair in 30 seconds. Technician sees full history.',
    's03-repair-intake.png', '/repair'),
  S(3, 3, 'Vendor RMA & Courier Tracking',
    'When a device goes back to Signia / Phonak — courier waybill, RMA number, and expected date all tracked.',
    'The day a courier misses SLA, you get an alert. Not next month when the patient calls asking.',
    's03-repair-vendor.png', '/repair'),
  S(3, 4, 'Loaner Allocation',
    'Automatic loaner assignment while the primary device is at the vendor.',
    'Patients don\'t walk around silent for two weeks. Your practice retains trust.',
    's03-repair-loaner.png', '/repair'),
  S(3, 5, 'Customer Approvals via WhatsApp',
    'Quotes for out-of-warranty repairs get sent + approved on WhatsApp with an audit trail.',
    'No verbal misunderstandings. Every rupee spent has a paper trail.',
    's03-repair-approval.png', '/repair'),
  S(3, 6, 'Delivered & Rated',
    'Ticket closes with a patient rating + technician notes. Ready for the next intake.',
    'Data compounds — you can spot which brand keeps coming back for the same fault.',
    's03-repair-delivered.png', '/repair'),

  // ── Section 4: HA & Inventory (4) ───────────────────────────────
  S(4, 1, 'Hearing-Aid Sales Pipeline',
    'Quote → Trial → Sale → Fitting → Warranty — one pipeline, every stage timestamped.',
    'See conversion drop-off. Rescue quotes before they go cold.',
    's04-ha-pipeline.png', '/ha'),
  S(4, 2, 'Serialised Inventory',
    'Every device is a serialised row: GRN, cost, MRP, MRP+GST, warranty end.',
    'When a patient asks "where\'s the second aid from my pair?" — you answer in 3 seconds.',
    's04-ha-inventory.png', '/ha'),
  S(4, 3, 'Trials in Progress',
    'Every active trial with day counter, next-appointment date, and outcome placeholder.',
    'Trial conversion is your #1 revenue lever — this screen makes it operational.',
    's04-ha-trials.png', '/ha'),
  S(4, 4, 'AMC & Warranty Ledger',
    'Auto-renewals, expiry alerts, and per-device service history.',
    'Warranty income becomes a real ledger, not a mental note.',
    's04-ha-amc.png', '/ha'),

  // ── Section 5: Revenue Dashboard (4) ────────────────────────────
  S(5, 1, 'Front-Desk Command Centre',
    'Live queue, appointments today, revenue-in-progress — the receptionist\'s single screen.',
    'The clinic runs even if you\'re not physically there.',
    's05-frontdesk.png', '/frontdesk'),
  S(5, 2, 'Clinical Analytics',
    'Test volumes, diagnosis mix, HA conversion — segmented by audiologist, by branch, by time.',
    'You finally see which clinician converts trials best. Reward them.',
    's05-analytics.png', '/analytics/clinical'),
  S(5, 3, 'Owner Dashboard',
    'Monthly revenue, receivables, HA vs Diagnostic vs AMC split, payments pending.',
    'The 6-metric owner view — no more asking the receptionist "how did we do this month?".',
    's05-owner.png', '/frontdesk'),
  S(5, 4, 'Payment Collection',
    'Razorpay-integrated payment links, cash-payment reconciliation, and daily deposit summary.',
    'Chase receivables with one tap. Reconcile with your bank in minutes.',
    's05-payments.png', '/billing'),

  // ── Section 6: Reports & Analytics (4) ──────────────────────────
  S(6, 1, 'Reports Library',
    'Every report — audiological, financial, operational, compliance — in one browsable index.',
    'A month-end close that used to take a day now takes 20 minutes.',
    's06-reports.png', '/reports'),
  S(6, 2, 'Conversion Funnel',
    'Walk-in → Diagnostic → Trial → Sale. Every step\'s drop-off surfaced.',
    'Find your leak. Fix the specific step that\'s bleeding revenue.',
    's06-funnel.png', '/analytics/clinical'),
  S(6, 3, 'Device Mix & Vendor Analysis',
    'Which brand / model sells best in your city. Which one has the highest repair rate.',
    'Buy inventory that actually moves. Drop brands that keep returning.',
    's06-devices.png', '/analytics/clinical'),
  S(6, 4, 'Scheduled Email Exports',
    'Weekly patient/invoice CSVs auto-emailed to your CA. End-of-month reports auto-generated.',
    'Reporting runs on cron. You never open Excel again.',
    's06-scheduled.png', '/settings/exports'),

  // ── Section 7: Referral Corner (5) ──────────────────────────────
  S(7, 1, 'Referring Doctor Directory',
    'Every ENT partner with a payout rule (% or flat), notify opt-ins, and lifetime referral stats.',
    'Structured relationships instead of hazy verbal deals.',
    's07-referrals-list.png', '/referrals'),
  S(7, 2, 'Pathway Tracking',
    'For every referred patient — did they show up? Test? Buy? Chips show real-time.',
    'Answer "what happened to the patient Dr. Iyer sent?" without a phone call.',
    's07-referrals-pathway.png', '/referrals'),
  S(7, 3, 'Doctor Drill-Down',
    'Per-doctor page with month-over-month volume, conversion, and payable amount.',
    'Show the doctor their own dashboard when they visit — they\'ll refer more.',
    's07-referrals-drilldown.png', '/referrals'),
  S(7, 4, 'Multi-Range Comparison',
    'Compare this quarter vs last quarter for a single doctor.',
    'Spot declining referrers before they go silent.',
    's07-referrals-compare.png', '/referrals'),
  S(7, 5, 'Automated WhatsApp Thank-Yous',
    'Every diagnostic + HA sale from an opted-in doctor triggers an automatic WhatsApp thank-you.',
    'Doctors feel valued without you remembering. Referral volume compounds.',
    's07-referrals-notify.png', '/referrals'),

  // ── Section 8: Settings (5) ─────────────────────────────────────
  S(8, 1, 'Clinic Profile & Branches',
    'Clinic name, GST, branches, staff schedule, MRD numbering. Multi-branch rollups included.',
    'Configure once, scale everywhere.',
    's08-settings-profile.png', '/settings/profile'),
  S(8, 2, 'Staff & Roles',
    'Add audiologist, front-desk, technician, accounts. Fine-grained permissions per role.',
    'A receptionist can\'t see revenue. An accountant can\'t sign audiograms. Clean data safety.',
    's08-settings-staff.png', '/settings/staff'),
  S(8, 3, 'Services & Packages',
    'Every test SKU, HA model, AMC package — priced, taxed, HSN-coded.',
    'Invoicing gets faster the more you configure. It\'s a one-time setup cost.',
    's08-settings-services.png', '/settings/services'),
  S(8, 4, 'Billing & Subscription',
    'Your AUDINEXA plan, upgrades, invoices — same tenant self-service card the founder demoed.',
    'Upgrade to Premium in 30 seconds. Downgrade the same way. No sales call.',
    's08-settings-billing.png', '/settings/billing'),
  S(8, 5, 'Integrations & Notifications',
    'WhatsApp (MSG91), Email (ZeptoMail), SMS (Twilio) — plug your keys and go live.',
    'You keep control of channels. You keep control of the data.',
    's08-settings-integrations.png', '/settings/integrations'),

  // ── Section 9: Security & Compliance (4) ────────────────────────
  S(9, 1, 'DPDPA Compliance Dashboard',
    'Encryption status, DPO email, data-request queue, breach playbook.',
    'You\'re not scrambling when a Data Principal Request lands.',
    's09-compliance.png', '/settings/compliance'),
  S(9, 2, 'Audit Log',
    'Every login, every export, every patient view — timestamped, tamper-proof.',
    'When a regulator asks who saw what and when — you have the answer in 30 seconds.',
    's09-audit.png', '/settings/compliance'),
  S(9, 3, 'One-Click Data Export',
    'Patient asks for a full data bundle → JSON + PDF handed over in one click.',
    'DPDPA right-to-portability, out of the box.',
    's09-export.png', '/data-export'),
  S(9, 4, 'Two-Factor Auth',
    'TOTP-based 2FA for owners and senior staff. Cookie-JWT + CSRF for every session.',
    'Even if someone shoulder-surfs your password, they don\'t get in.',
    's09-mfa.png', '/settings/security'),

  // ── Section 10: Support (3) ─────────────────────────────────────
  S(10, 1, 'In-App Support Desk',
    'Raise a ticket from any screen. Automatic screenshot + browser context attached.',
    'Bug reports become debuggable — no more "it\'s broken" WhatsApp forwards.',
    's10-support-desk.png', '/settings/support'),
  S(10, 2, 'Onboarding Call — Included',
    'Every new signup gets a 30-minute onboarding call. CSV migration handled by us.',
    'You\'re running the platform on day 3, not day 30.',
    's10-onboarding.png', '/settings/support'),
  S(10, 3, 'Direct Founder Access',
    'Escalate any issue to the founder\'s inbox. Response within 4 business hours.',
    'You\'re not customer #4,231 — you\'re a founding user.',
    's10-founder.png', '/settings/support'),
];

// Sanity: 46 slides across 10 sections. Update this if you add/remove.
if (typeof process === 'undefined' || process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-console
  console.debug('[demo] slides:', SLIDES.length, 'sections:', SECTIONS.length);
}
